"use client";

import { ReactNode, useRef } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import {
  getDataAutomationGroup,
  getDataAutomationId,
  getDataLinkAttributes,
} from "@/helpers";

export interface ConsolidatedDropdownProps {
  ariaLabel: string;
  icon: ReactNode;
  dld?: string;
  dlt?: string;
  automationId: string;
  buttonClassName?: string;
  children: ReactNode;
  opensOnHover?: boolean;
  timeoutDuration?: number;
}

const ConsolidatedDropdown: React.FC<ConsolidatedDropdownProps> = ({
  ariaLabel,
  icon,
  dld,
  dlt,
  automationId,
  buttonClassName,
  children,
  opensOnHover = false,
  timeoutDuration = 120,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const timeOutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Simulates a physical pointer click to bypass virtual click focus-ring
  const openByPointer = () => {
    const el = triggerRef.current;
    if (!el) return;

    el.dispatchEvent(
      new PointerEvent("click", {
        bubbles: true,
        cancelable: true,
        detail: 1,
        pointerType: "mouse",
      }),
    );
  };

  const handleEnter = (isOpen: boolean) => {
    clearTimeout(timeOutRef.current);
    if (!isOpen) {
      openByPointer();
    }
  };

  const handleLeave = (isOpen: boolean) => {
    timeOutRef.current = setTimeout(() => {
      if (isOpen) {
        openByPointer();
      }
    }, timeoutDuration);
  };

  if (opensOnHover) {
    return (
      <Popover className="relative">
        {({ open }) => (
          <div
            onMouseEnter={() => handleEnter(open)}
            onMouseLeave={() => handleLeave(open)}
          >
            <PopoverButton
              ref={triggerRef}
              aria-label={ariaLabel}
              className={buttonClassName}
              {...getDataLinkAttributes({ dld, dlt })}
              {...getDataAutomationId(automationId)}
            >
              {icon}
            </PopoverButton>
            <PopoverPanel>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="8"
                viewBox="0 0 14 6"
                fill="none"
                className="pointer-events-none absolute top-full left-1/2 z-20 mt-1 -translate-x-1/2"
                aria-hidden="true"
              >
                <path
                  d="M6.92773 0L13.8559 6H-0.000468731L6.92773 0Z"
                  fill="white"
                />
              </svg>
              <ul
                className="absolute -right-[3.125rem] z-10 mt-[0.6875rem] flex flex-col gap-3 rounded-lg bg-white px-6 py-4 shadow-[0_0_40px_0_rgba(0,0,0,0.2)]"
                {...getDataAutomationGroup("panel")}
              >
                {children}
              </ul>
            </PopoverPanel>
          </div>
        )}
      </Popover>
    );
  }

  return (
    <Popover className="relative">
      <PopoverButton
        aria-label={ariaLabel}
        className={buttonClassName}
        {...getDataLinkAttributes({ dld, dlt })}
        {...getDataAutomationId(automationId)}
      >
        {icon}
      </PopoverButton>
      <PopoverPanel>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="8"
          viewBox="0 0 14 6"
          fill="none"
          className="pointer-events-none absolute top-full left-1/2 z-20 mt-1 -translate-x-1/2"
          aria-hidden="true"
        >
          <path d="M6.92773 0L13.8559 6H-0.000468731L6.92773 0Z" fill="white" />
        </svg>
        <ul
          className="absolute -right-[3.125rem] z-10 mt-[0.6875rem] flex flex-col gap-3 rounded-lg bg-white px-6 py-4 shadow-[0_0_40px_0_rgba(0,0,0,0.2)]"
          {...getDataAutomationGroup("panel")}
        >
          {children}
        </ul>
      </PopoverPanel>
    </Popover>
  );
};

export default ConsolidatedDropdown;
